import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { QUOTE_PRODUCT_MAX_QUANTITY } from '../../../../mappings';
import theme from '../../../../theme';
import { QuoteProductQuantityProps } from './QuoteProductQuantity.props';
import * as Sx from './QuoteProductQuantity.styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NumericFormatCustom = forwardRef<NumericFormatProps, any>(
  function NumericFormatCustom(props, ref) {
    const { onChange, currencysymbol, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({ target: { value: values.value } });
        }}
        fixedDecimalScale
        thousandSeparator
        decimalScale={2}
        allowNegative={false}
        valueIsNumericString
        prefix={currencysymbol}
      />
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IntegerThousandSeparatorFormat = forwardRef<NumericFormatProps, any>(
  function IntegerThousandSeparatorFormat(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({ target: { value: values.value } });
        }}
        thousandSeparator
        decimalScale={0}
        allowNegative={false}
        valueIsNumericString
      />
    );
  }
);

const numericValueIsInvalid = (min: number, max: number, value?: number) =>
  value === undefined || value < min || value > max;

export const QuoteProductQuantity = ({
  onChange,
  quantity,
  disabled,
  showError = true,
  inputAdornment,
  width = '130px',
  maxValue = QUOTE_PRODUCT_MAX_QUANTITY,
  minValue = 1,
  currencyFormatter,
  numberValidation,
  thousandSeparator,
}: QuoteProductQuantityProps) => {
  const InputComponent = currencyFormatter
    ? NumericFormatCustom
    : thousandSeparator
    ? IntegerThousandSeparatorFormat
    : undefined;
  const [localValue, setLocalValue] = useState(quantity?.toString() ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const numberValidationMessage = useMemo(
    () => (numberValidation ? numberValidation(quantity) : null),
    [numberValidation, quantity]
  );

  // Only update localValue from prop when not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(quantity?.toString() ?? '');
    }
  }, [quantity, isEditing]);

  // https://github.com/mui/material-ui/issues/7960
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const triggerUpdate = () => {
    setIsEditing(false);
    onChange(localValue);
  };

  return (
    <TextField
      required
      variant="outlined"
      type={InputComponent ? 'text' : 'number'}
      disabled={disabled}
      value={localValue}
      error={
        (showError && numericValueIsInvalid(minValue, maxValue, quantity)) ||
        !!numberValidationMessage
      }
      sx={{ width, ...Sx.textFieldNumericStyles }}
      onFocus={(event) => event.target.select()}
      onChange={(e) => {
        setIsEditing(true);
        setLocalValue(e.target.value);
      }}
      onBlur={triggerUpdate}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          triggerUpdate();
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      InputLabelProps={{
        shrink: true,
      }}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        currencysymbol: currencyFormatter?.getSymbol(),
        ref: inputRef,
      }}
      InputProps={{
        inputComponent: InputComponent,
        ...(inputAdornment
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ opacity: 0.6 }}>
                    {inputAdornment}
                  </Typography>
                  {!!numberValidationMessage && (
                    <Tooltip
                      sx={Sx.toolTipError}
                      title={numberValidationMessage}
                    >
                      <ErrorOutlineIcon
                        htmlColor={theme.palette.error.main}
                        fontSize="small"
                      />
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }
          : undefined),
      }}
    />
  );
};
